import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import { useCollectionSchema, useReferenceData } from "../hooks/useCollectionSchema";
import DynamicForm from "../components/DynamicForm";

const InventoryPage = () => {
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTool, setEditingTool] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("all");
    const [formData, setFormData] = useState({});

    // Get dynamic schema
    const { fields, displayName, loading: schemaLoading } = useCollectionSchema("tools");
    const { references } = useReferenceData(fields);

    // Get category options from schema
    const categoryField = fields.find(f => f.name === "category");
    const categories = categoryField?.options || [
        "Herramientas manuales",
        "Herramientas eléctricas",
        "Equipos de seguridad",
        "Materiales",
        "Consumibles",
        "Otros"
    ];

    useEffect(() => {
        fetchTools();
    }, []);

    // Initialize form data when fields change
    useEffect(() => {
        if (fields.length > 0 && !editingTool) {
            initializeFormData();
        }
    }, [fields]);

    const initializeFormData = () => {
        const initial = {};
        fields.forEach(field => {
            initial[field.name] = field.default ?? (field.type === "number" ? 0 : "");
        });
        setFormData(initial);
    };

    const fetchTools = async () => {
        try {
            const response = await axios.get(`${API}/tools`);
            setTools(response.data);
        } catch (error) {
            toast.error("Error al cargar el inventario");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const missingRequired = fields
            .filter(f => f.required && (formData[f.name] === "" || formData[f.name] === undefined))
            .map(f => f.label || f.name);
        
        if (missingRequired.length > 0) {
            toast.error(`Campos requeridos: ${missingRequired.join(", ")}`);
            return;
        }

        try {
            if (editingTool) {
                await axios.put(`${API}/tools/${editingTool.id}`, formData);
                toast.success("Herramienta actualizada");
            } else {
                await axios.post(`${API}/tools`, formData);
                toast.success("Herramienta añadida");
            }
            fetchTools();
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al guardar");
        }
    };

    const handleEdit = (tool) => {
        setEditingTool(tool);
        const data = {};
        fields.forEach(field => {
            data[field.name] = tool[field.name] ?? field.default ?? (field.type === "number" ? 0 : "");
        });
        setFormData(data);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar esta herramienta?")) return;
        try {
            await axios.delete(`${API}/tools/${id}`);
            toast.success("Herramienta eliminada");
            fetchTools();
        } catch (error) {
            toast.error("Error al eliminar");
        }
    };

    const resetForm = () => {
        setEditingTool(null);
        initializeFormData();
    };

    const filteredTools = tools.filter((tool) => {
        const matchesSearch = (tool.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (tool.category || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === "all" || tool.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const lowStockCount = tools.filter(t => t.quantity <= (t.min_stock || 0)).length;

    // Get visible columns from schema (exclude notes and other text fields for table)
    const tableFields = fields.filter(f => f.type !== "text" && f.type !== "array").slice(0, 5);

    if (loading || schemaLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in" data-testid="inventory-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">{displayName}</h1>
                    <p className="text-[#555555] mt-1">
                        {tools.length} herramientas registradas
                        {lowStockCount > 0 && (
                            <span className="ml-2 text-red-600">
                                ({lowStockCount} con stock bajo)
                            </span>
                        )}
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-tool-btn">
                            <Plus size={20} className="mr-2" />
                            Añadir herramienta
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {editingTool ? "Editar herramienta" : "Nueva herramienta"}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-4">
                            <DynamicForm 
                                fields={fields}
                                formData={formData}
                                setFormData={setFormData}
                                references={references}
                            />
                            <div className="flex gap-3 pt-6">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid="tool-save-btn">
                                    {editingTool ? "Guardar cambios" : "Añadir"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-industrial pl-10"
                        placeholder="Buscar herramientas..."
                        data-testid="search-tools-input"
                    />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="input-industrial w-full md:w-48" data-testid="filter-category-select">
                        <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="table-industrial">
                                {tableFields.map(field => (
                                    <TableHead key={field.name} className={field.type === "number" ? "text-center" : ""}>
                                        {field.label || field.name}
                                    </TableHead>
                                ))}
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredTools.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableFields.length + 1} className="text-center py-12 text-[#555555]">
                                        No hay herramientas que coincidan con la búsqueda
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredTools.map((tool) => (
                                    <TableRow key={tool.id} className="table-industrial" data-testid={`tool-row-${tool.id}`}>
                                        {tableFields.map(field => {
                                            const value = tool[field.name];
                                            const isLowStock = field.name === "quantity" && value <= (tool.min_stock || 0);
                                            
                                            return (
                                                <TableCell 
                                                    key={field.name}
                                                    className={field.type === "number" ? "text-center" : ""}
                                                >
                                                    {field.name === "name" ? (
                                                        <div className="flex items-center gap-2">
                                                            {tool.quantity <= (tool.min_stock || 0) && (
                                                                <Warning size={18} className="text-red-500" weight="fill" />
                                                            )}
                                                            <span className="font-medium">{value}</span>
                                                        </div>
                                                    ) : field.name === "quantity" ? (
                                                        <>
                                                            <span className={`font-bold ${isLowStock ? "text-red-600" : ""}`}>
                                                                {value}
                                                            </span>
                                                            <span className="text-[#555555] text-sm ml-1">/ {tool.min_stock || 0} min</span>
                                                        </>
                                                    ) : field.name === "category" ? (
                                                        <span className="badge-info">{value}</span>
                                                    ) : (
                                                        value || "-"
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(tool)}
                                                    className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                                                    data-testid={`edit-tool-${tool.id}`}
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(tool.id)}
                                                    className="p-2 hover:bg-red-50 text-red-600 rounded-sm"
                                                    data-testid={`delete-tool-${tool.id}`}
                                                >
                                                    <Trash size={18} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default InventoryPage;
